import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, message, Modal, Popconfirm, Radio, Space, Spin, Tag, Typography } from "antd";
import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserContext from "../../../../Contexts/UserContext";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";
import { checkRuleHasTransactions, deactivateRule, deleteRule, getRuleById, getTransfersByRuleId, reverseRule } from "./psrlTransferRulesService";
import TransfersTable from "./TransfersTable";

const { Title } = Typography;

const PSRLRuleView = () => {
  const { auth } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [rule, setRule] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [hasTransactions, setHasTransactions] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [deactivateOption, setDeactivateOption] = useState('deactivateOnly');
  const navigate = useNavigate();
  const { id } = useParams();

  const loadRule = useCallback(async () => {
    try {
      setLoading(true);
      const ruleData = await getRuleById(parseInt(id));
      if (ruleData) {
        setRule(ruleData);
        // No need to load agencies and agents separately since the backend already provides the names
      } else {
        message.error("Rule not found");
        navigate("/settings?tab=3&subtab=2");
      }
    } catch (err) {
      message.error(err.message || "Failed to load rule");
      navigate("/settings?tab=3&subtab=2");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadTransfers = useCallback(async () => {
    try {
      setTransfersLoading(true);
      const transfersData = await getTransfersByRuleId(parseInt(id));
      setTransfers(transfersData || []);
    } catch (err) {
      console.error("Failed to load transfers:", err);
      message.error("Failed to load transfers for this rule");
      setTransfers([]);
    } finally {
      setTransfersLoading(false);
    }
  }, [id]);

  const checkRuleUsage = useCallback(async () => {
    try {
      const { hasTransactions: hasTransfers } = await checkRuleHasTransactions(parseInt(id));
      setHasTransactions(hasTransfers);
    } catch (err) {
      console.error("Failed to check rule usage:", err);
      setHasTransactions(false);
    }
  }, [id]);


  useEffect(() => {
    if (auth?.token && id) {
      loadRule();
      loadTransfers();
      checkRuleUsage();
    }
  }, [auth?.token, id, loadRule, loadTransfers, checkRuleUsage]);


  const handleEdit = () => {
    navigate(`/settings/transfer-rules/edit/${id}`);
  };

  const handleBack = () => {
    navigate("/settings?tab=3&subtab=2");
  };

  const handleDeactivateWithOptions = () => {
    setDeactivateOption('deactivateOnly'); // Reset to default
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = async () => {
    try {
      setActionLoading(true);
      if (deactivateOption === 'deactivateOnly') {
        await deactivateRule(parseInt(id));
        message.success("Rule deactivated successfully");
      } else {
        await reverseRule(parseInt(id));
        message.success("Rule deactivated and transactions reversed successfully");
      }
      // Reload rule data to reflect changes
      await loadRule();
      await loadTransfers();
      await checkRuleUsage();
    } catch (err) {
      message.error(err.message || "Failed to deactivate rule");
    } finally {
      setActionLoading(false);
      setIsDeactivateModalOpen(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await deleteRule(parseInt(id));
      message.success("Rule deleted successfully");
      navigate("/settings?tab=3&subtab=2");
    } catch (err) {
      message.error(err.message || "Failed to delete rule");
    } finally {
      setActionLoading(false);
    }
  };


  if (loading) {
    return (
      <AdminAppLayout>
        <div
          style={{
            paddingLeft: 152,
            paddingRight: 152,
            marginTop: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <Spin size="large" />
        </div>
      </AdminAppLayout>
    );
  }

  if (!rule) {
    return (
      <AdminAppLayout>
        <div
          className="page-content-wrapper"
          style={{
            textAlign: 'center'
          }}
        >
          <Title level={4}>Rule not found</Title>
          <Button onClick={handleBack}>Back to Rules</Button>
        </div>
      </AdminAppLayout>
    );
  }

  return (
    <AdminAppLayout>
        <div className="page-content-wrapper">
        <Title level={4} style={{ color: colors.black, marginBottom: 24 }}>
          Transfer Rule Details
        </Title>
        
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            Back to Rules
          </Button>
          
          {/* Always show Modify button */}
          <Button 
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
            disabled={rule?.PSRL_Is_Reversed}
            loading={actionLoading}
          >
            Modify
          </Button>
          
          {/* Deactivate Rule button with modal options */}
          <Button 
            icon={<StopOutlined />}
            onClick={handleDeactivateWithOptions}
            disabled={!rule?.PSRL_Active || rule?.PSRL_Is_Reversed}
            loading={actionLoading}
          >
            Deactivate Rule
          </Button>
          
          {/* Show Delete button only if rule has no transactions */}
          {!hasTransactions && (
            <Popconfirm
              title="Delete Rule"
              description="This will permanently delete this rule. Are you sure?"
              onConfirm={handleDelete}
              okText="Yes, Delete"
              cancelText="Cancel"
            >
              <Button 
                icon={<DeleteOutlined />}
                danger
                loading={actionLoading}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>

        <Card>
          <Descriptions
            title="Rule Information"
            bordered
            column={2}
            size="middle"
            labelStyle={{ 
              fontWeight: 'bold',
              backgroundColor: '#fafafa',
              width: '200px'
            }}
          >
            <Descriptions.Item label="Transfer Type" span={2}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {rule.PSRL_TransferType ? rule.PSRL_TransferType.replace(/_/g, ' ') : 'Unknown'}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label="Status" span={2}>
              <Tag color={
                rule.PSRL_Is_Reversed ? "orange" : 
                rule.PSRL_Active ? "green" : "red"
              } style={{ fontSize: '14px', padding: '4px 12px' }}>
                {rule.PSRL_Is_Reversed ? "Reversed" : 
                 rule.PSRL_Active ? "Active" : "Inactive"}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label="Rule Name" span={2}>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                {rule.PSRL_Rule_Name || 'Unnamed Rule'}
              </div>
            </Descriptions.Item>
            
            {rule.PSRL_Note && (
              <Descriptions.Item label="Rule Notes" span={2}>
                <div style={{ fontSize: '14px', lineHeight: '1.5', color: '#666' }}>
                  {rule.PSRL_Note}
                </div>
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="From Agency" span={2}>
              <div style={{ fontSize: '16px' }}>
                {rule.ForAgencyName || 'N/A'}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="To Agent" span={2}>
              <div style={{ fontSize: '16px' }}>
                {rule.TransferToAgentName ? `${rule.TransferToAgentName}${rule.TransferToAgentEmail ? ` (${rule.TransferToAgentEmail})` : ''}` : 'N/A'}
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Transfer Percentage" span={2}>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {rule.PSRL_TransferPointsPercentage || 100}%
                </Tag>
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Rule Start Date">
              <div style={{ fontSize: '16px' }}>
                {rule.PSRL_Rule_From_Date 
                  ? dayjs(rule.PSRL_Rule_From_Date).format('MMMM DD, YYYY')
                  : 'No start date (Active from beginning)'
                }
              </div>
            </Descriptions.Item>
            
            <Descriptions.Item label="Rule End Date">
              <div style={{ fontSize: '16px' }}>
                {rule.PSRL_Rule_To_Date 
                  ? dayjs(rule.PSRL_Rule_To_Date).format('MMMM DD, YYYY')
                  : 'No end date (Active indefinitely)'
                }
              </div>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Transfers Table */}
        <div style={{ marginTop: 24 }}>
          <TransfersTable 
            transfers={transfers}
            loading={transfersLoading}
            title="Associated Transfers"
          />
        </div>
      </div>

      {/* Deactivate Rule Modal */}
      <Modal
        open={isDeactivateModalOpen}
        title="Deactivate Transfer Rule"
        width={520}
        centered
        closable={true}
        onCancel={() => setIsDeactivateModalOpen(false)}
        onOk={handleDeactivateConfirm}
        okText="Deactivate"
        cancelText="Cancel"
        className="deactivate-modal"
        okButtonProps={{ loading: actionLoading, type: 'primary' }}
              cancelButtonProps={{ disabled: actionLoading }}
              style={{ marginBottom: '16px', paddingLeft:'20px' }}
      >
        <p style={{ marginBottom: '16px', fontSize: '14px', lineHeight: '1.5' }}>
          Choose how to deactivate this rule:
        </p>
        <Radio.Group 
          value={deactivateOption} 
          onChange={(e) => setDeactivateOption(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Radio value="deactivateOnly">
              <div>
                <strong>Deactivate only</strong>
                <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                  Rule will be deactivated but all existing transactions remain unchanged
                </div>
              </div>
            </Radio>
            <Radio value="deactivateAndReverse">
              <div>
                <strong>Deactivate and reverse all transactions</strong>
                <div style={{ color: '#666', fontSize: '13px', marginTop: '4px' }}>
                  Rule will be deactivated and all associated transactions will be reversed
                </div>
              </div>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </AdminAppLayout>
  );
};

export default PSRLRuleView;
